import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMarketingChain } from '../../../hooks/useMarketingChain';
import { useSessionPersistence } from '../../../hooks/useSessionPersistence';
import ResumeSessionDialog from './ResumeSessionDialog';
import HistoryList from './HistoryList';
import ConfigurationStep from './ConfigurationStep';
import AnalysisStep from './AnalysisStep';
import StrategySelectionStep from './StrategySelectionStep';
import TacticsDisplayStep from './TacticsDisplayStep';
import ProgressIndicator from './ProgressIndicator';
import LoadingSpinner from './LoadingSpinner';
import AnalysisLoadingStep from './AnalysisLoadingStep';
import ErrorMessage from './ErrorMessage';

const MarketingChainWizard = ({ userId, onComplete, onError }) => {
    const [currentStep, setCurrentStep] = useState('configuring');
    const [sessionId, setSessionId] = useState(null);
    const [searchParams] = useSearchParams();
    const abortControllerRef = useRef(null);
    const isInitializedRef = useRef(false);
    const [showResumeDialog, setShowResumeDialog] = useState(false);
    const [resumeData, setResumeData] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    const {
        session,
        loading,
        error,
        startAnalysis,
        selectObjective,
        selectStrategy,
        getSessionStatus,
        setSession,
        checkResumeSession,
        shouldUseExistingSession
    } = useMarketingChain(userId);
    const { saveSession, getSavedSession, clearSession } = useSessionPersistence();

    const loadExistingSession = useCallback(async (sessionId) => {
        // Cancelar petición anterior si existe
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Crear nuevo AbortController
        abortControllerRef.current = new AbortController();

        try {
            await getSessionStatus(sessionId, abortControllerRef.current.signal);
        } catch (error) {
            // Solo manejar errores que no sean de cancelación
            if (error.name !== 'AbortError') {
                console.error('Error loading existing session:', error);
                clearSession();
                setSessionId(null);
            }
        }
    }, [getSessionStatus, clearSession]);

    // Verificar si hay una sesión guardada al cargar o en la URL
    useEffect(() => {
        // Evitar múltiples inicializaciones
        if (isInitializedRef.current) {
            return;
        }

        const initializeSession = async () => {
            const urlSessionId = searchParams.get('session');
            const savedSessionId = getSavedSession();

            if (urlSessionId) {
                // Prioridad a la sesión de la URL
                setSessionId(urlSessionId);
                loadExistingSession(urlSessionId);
                isInitializedRef.current = true;
            } else if (savedSessionId) {
                setSessionId(savedSessionId);
                loadExistingSession(savedSessionId);
                isInitializedRef.current = true;
            } else {
                // Verificar si hay sesión pendiente en el servidor
                try {
                    const resumeData = await checkResumeSession();
                    if (resumeData && shouldUseExistingSession(resumeData)) {
                        setResumeData(resumeData);
                        setShowResumeDialog(true);
                        isInitializedRef.current = true;
                    }
                } catch (error) {
                    console.error('Error verificando sesión pendiente:', error);
                }
            }
        };

        initializeSession();
    }, [searchParams, getSavedSession, loadExistingSession, checkResumeSession, shouldUseExistingSession]);

    // Actualizar el paso actual basado en el estado de la sesión
    useEffect(() => {
        if (session) {
            updateCurrentStep(session.status);
        }
    }, [session]);

    // Polling para verificar el estado del análisis
    useEffect(() => {
        let interval;
        let isPolling = false;

        if (sessionId && currentStep === 'analyzing' && !loading && !isPolling) {
            isPolling = true;
            interval = setInterval(async () => {
                try {
                    console.log('Verificando estado de sesión:', sessionId);
                    const updatedSession = await getSessionStatus(sessionId);
                    console.log('Estado actualizado:', updatedSession);

                    if (updatedSession.status !== 'analyzing') {
                        // El análisis ha terminado, actualizar el estado
                        setSession(updatedSession);
                        clearInterval(interval);
                        isPolling = false;
                    }
                } catch (error) {
                    console.error('Error al verificar estado:', error);
                    // Solo manejar errores que no sean de cancelación
                    if (error.name === 'AbortError') {
                        clearInterval(interval);
                        isPolling = false;
                    }
                }
            }, 5000); // Verificar cada 5 segundos
        }

        return () => {
            if (interval) {
                clearInterval(interval);
                isPolling = false;
            }
        };
    }, [sessionId, currentStep, loading, getSessionStatus, setSession]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateCurrentStep = (status) => {
        switch (status) {
            case 'analyzing':
                setCurrentStep('analyzing');
                break;
            case 'objectives_ready':
                setCurrentStep('selecting_objective');
                break;
            case 'strategy_ready':
                setCurrentStep('selecting_strategy');
                break;
            case 'tactics_ready':
                setCurrentStep('viewing_tactics');
                break;
            case 'completed':
                setCurrentStep('completed');
                break;
            case 'error':
                setCurrentStep('error');
                break;
            default:
                setCurrentStep('configuring');
        }
    };

    const handleConfigurationSubmit = async (config) => {
        try {
            console.log('Iniciando análisis con configuración:', config);
            const result = await startAnalysis(config);
            console.log('Análisis iniciado exitosamente:', result);
            setSessionId(result.sessionId);
            saveSession(result.sessionId);
            setCurrentStep('analyzing');
        } catch (error) {
            console.error('Error al iniciar análisis:', error);
            if (onError) onError(error.message || 'Error al iniciar análisis');
        }
    };

    const handleObjectiveSelect = async (objectiveId) => {
        try {
            await selectObjective(objectiveId);
            setCurrentStep('selecting_strategy');
        } catch (error) {
            // Solo mostrar error si no es un mensaje de éxito
            if (onError && !error.message.includes('correctamente')) {
                onError(error.message);
            }
        }
    };

    const handleStrategySelect = async (strategyId) => {
        try {
            await selectStrategy(strategyId);
            setCurrentStep('viewing_tactics');
        } catch (error) {
            // Solo mostrar error si no es un mensaje de éxito
            if (onError && !error.message.includes('correctamente')) {
                onError(error.message);
            }
        }
    };

    const handleComplete = () => {
        if (onComplete) onComplete(session);
        setCurrentStep('completed');
    };

    const handleExportTactics = () => {
        if (!session.tactics || session.tactics.length === 0) {
            alert('No hay tácticas para exportar');
            return;
        }

        // Crear contenido del PDF
        const content = {
            title: 'Plan de Marketing - Tácticas Generadas',
            date: new Date().toLocaleDateString('es-ES'),
            objective: session.selectedObjective?.title || 'Objetivo no especificado',
            strategy: session.selectedStrategy?.title || 'Estrategia no especificada',
            tactics: session.tactics.map((tactic, index) => ({
                number: index + 1,
                title: tactic.title,
                description: tactic.description,
                platform: tactic.platform,
                budget: tactic.budget,
                timeline: tactic.timeline
            }))
        };

        // Crear el documento
        const doc = {
            content: [
                { text: content.title, style: 'header' },
                { text: `Generado el: ${content.date}`, style: 'subheader' },
                { text: `Objetivo: ${content.objective}`, style: 'subheader' },
                { text: `Estrategia: ${content.strategy}`, style: 'subheader' },
                { text: '', margin: [0, 20, 0, 0] },
                ...content.tactics.map(tactic => [
                    { text: `Táctica ${tactic.number}: ${tactic.title}`, style: 'tacticTitle' },
                    { text: tactic.description, style: 'tacticDescription' },
                    {
                        text: [
                            { text: 'Plataforma: ', bold: true },
                            { text: tactic.platform || 'No especificada' },
                            { text: ' | Presupuesto: ', bold: true },
                            { text: tactic.budget || 'No especificado' },
                            { text: ' | Timeline: ', bold: true },
                            { text: tactic.timeline || 'No especificado' }
                        ],
                        style: 'tacticMeta'
                    },
                    { text: '', margin: [0, 10, 0, 0] }
                ]).flat()
            ],
            styles: {
                header: {
                    fontSize: 20,
                    bold: true,
                    margin: [0, 0, 0, 10],
                    alignment: 'center'
                },
                subheader: {
                    fontSize: 14,
                    bold: true,
                    margin: [0, 10, 0, 5]
                },
                tacticTitle: {
                    fontSize: 16,
                    bold: true,
                    margin: [0, 15, 0, 5],
                    color: '#2563eb'
                },
                tacticDescription: {
                    fontSize: 12,
                    margin: [0, 5, 0, 5]
                },
                tacticMeta: {
                    fontSize: 10,
                    margin: [0, 5, 0, 0],
                    color: '#6b7280'
                }
            }
        };

        // Descargar como PDF (simulado)
        const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `plan-marketing-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        alert('Plan exportado correctamente. En una implementación real, esto generaría un PDF.');
    };

    const handleShareTactics = () => {
        if (!session.tactics || session.tactics.length === 0) {
            alert('No hay tácticas para compartir');
            return;
        }

        // Crear resumen para compartir
        const summary = {
            title: 'Plan de Marketing Generado',
            objective: session.selectedObjective?.title || 'Objetivo no especificado',
            strategy: session.selectedStrategy?.title || 'Estrategia no especificada',
            tacticsCount: session.tactics.length,
            tactics: session.tactics.slice(0, 3).map(tactic => tactic.title) // Solo las primeras 3
        };

        const shareText = `
🎯 Plan de Marketing Generado

📋 Objetivo: ${summary.objective}
🚀 Estrategia: ${summary.strategy}
📊 Tácticas Generadas: ${summary.tacticsCount}

🔝 Principales Tácticas:
${summary.tactics.map((title, index) => `${index + 1}. ${title}`).join('\n')}

${summary.tactics.length < session.tactics.length ? `... y ${session.tactics.length - summary.tactics.length} tácticas más` : ''}

Generado con IA Marketing Assistant
        `.trim();

        // Intentar usar la API de compartir nativa
        if (navigator.share) {
            navigator.share({
                title: summary.title,
                text: shareText,
                url: window.location.href
            }).catch(err => {
                console.log('Error al compartir:', err);
                fallbackShare(shareText);
            });
        } else {
            fallbackShare(shareText);
        }
    };

    const fallbackShare = (text) => {
        // Copiar al portapapeles
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).then(() => {
                alert('Plan copiado al portapapeles. Puedes pegarlo donde quieras compartirlo.');
            }).catch(() => {
                // Fallback para navegadores más antiguos
                const textArea = document.createElement('textarea');
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                alert('Plan copiado al portapapeles. Puedes pegarlo donde quieras compartirlo.');
            });
        } else {
            // Mostrar el texto para copiar manualmente
            alert(`Plan listo para compartir:\n\n${text}`);
        }
    };

    const handleRestart = () => {
        // Cancelar peticiones pendientes
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        clearSession();
        setSessionId(null);
        setCurrentStep('configuring');
        isInitializedRef.current = false;
        setShowResumeDialog(false);
        setResumeData(null);
    };

    const handleContinueSession = () => {
        if (resumeData) {
            setSession(resumeData);
            setSessionId(resumeData.sessionId);
            saveSession(resumeData.sessionId);
            updateCurrentStep(resumeData.status);
            setShowResumeDialog(false);
            setResumeData(null);
        }
    };

    const handleStartNewSession = () => {
        setShowResumeDialog(false);
        setResumeData(null);
        setCurrentStep('configuring');
    };

    const handleShowHistory = () => {
        setShowHistory(true);
    };

    const handleHideHistory = () => {
        setShowHistory(false);
    };

    const handleSelectProposal = (sessionId) => {
        // Cargar la propuesta seleccionada
        setSessionId(sessionId);
        loadExistingSession(sessionId);
        setShowHistory(false);
    };

    const handleContinueProposal = (proposal) => {
        setSession(proposal);
        setSessionId(proposal.sessionId);
        saveSession(proposal.sessionId);
        updateCurrentStep(proposal.status);
        setShowHistory(false);
    };

    // Cleanup al desmontar el componente
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const steps = [
        { id: 'configuring', title: 'Configuración', status: currentStep === 'configuring' ? 'active' : currentStep === 'configuring' ? 'completed' : 'pending' },
        { id: 'analyzing', title: 'Análisis', status: currentStep === 'analyzing' ? 'active' : ['selecting_objective', 'selecting_strategy', 'viewing_tactics', 'completed'].includes(currentStep) ? 'completed' : 'pending' },
        { id: 'selecting_objective', title: 'Objetivo', status: currentStep === 'selecting_objective' ? 'active' : ['selecting_strategy', 'viewing_tactics', 'completed'].includes(currentStep) ? 'completed' : 'pending' },
        { id: 'selecting_strategy', title: 'Estrategia', status: currentStep === 'selecting_strategy' ? 'active' : ['viewing_tactics', 'completed'].includes(currentStep) ? 'completed' : 'pending' },
        { id: 'viewing_tactics', title: 'Tácticas', status: currentStep === 'viewing_tactics' ? 'active' : currentStep === 'completed' ? 'completed' : 'pending' }
    ];

    if (error && currentStep !== 'error') {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <ErrorMessage
                    message={error}
                    onRetry={() => window.location.reload()}
                    onRestart={handleRestart}
                />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Asistente de Marketing Inteligente
                        </h1>
                        <p className="text-gray-600">
                            Genera estrategias de marketing personalizadas basadas en los datos de tu negocio
                        </p>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={handleShowHistory}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Historial
                        </button>
                    </div>
                </div>
            </div>

            <ProgressIndicator currentStep={currentStep} steps={steps} />

            <div className="mt-8">
                {loading && currentStep === 'analyzing' && (
                    <AnalysisLoadingStep sessionId={sessionId} />
                )}

                {loading && currentStep !== 'analyzing' && (
                    <LoadingSpinner message="Procesando..." />
                )}

                {!loading && currentStep === 'configuring' && (
                    <ConfigurationStep
                        onNext={handleConfigurationSubmit}
                        loading={loading}
                    />
                )}

                {!loading && currentStep === 'analyzing' && session && (
                    <AnalysisStep
                        sessionId={sessionId}
                        businessAnalysis={session.businessAnalysis}
                        objectives={session.tangibleObjectives}
                        onObjectiveSelect={handleObjectiveSelect}
                        loading={loading}
                    />
                )}

                {!loading && currentStep === 'selecting_objective' && session && (
                    <AnalysisStep
                        sessionId={sessionId}
                        businessAnalysis={session.businessAnalysis}
                        objectives={session.tangibleObjectives}
                        onObjectiveSelect={handleObjectiveSelect}
                        loading={loading}
                    />
                )}

                {!loading && currentStep === 'selecting_strategy' && session && (
                    <>
                        {console.log('Session data:', session)}
                        {console.log('Strategies data:', session.strategies)}
                        <StrategySelectionStep
                            sessionId={sessionId}
                            strategies={session.strategies}
                            onStrategySelect={handleStrategySelect}
                            loading={loading}
                        />
                    </>
                )}

                {!loading && currentStep === 'viewing_tactics' && session && (
                    <TacticsDisplayStep
                        tactics={session.tactics}
                        onExport={handleExportTactics}
                        onShare={handleShareTactics}
                        onImplement={(tacticId) => {/* Implementar acción */ }}
                        onComplete={handleComplete}
                    />
                )}

                {!loading && currentStep === 'completed' && (
                    <div className="text-center py-12">
                        <div className="text-green-500 text-6xl mb-4">✓</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            ¡Plan de Marketing Completado!
                        </h2>
                        <p className="text-gray-600 mb-8">
                            Tu estrategia de marketing personalizada está lista para implementar.
                        </p>
                        <div className="space-x-4">
                            <button
                                onClick={handleRestart}
                                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Crear Nueva Estrategia
                            </button>
                            <button
                                onClick={handleShowHistory}
                                className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Ver Historial
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Resume Session Dialog */}
            {showResumeDialog && resumeData && (
                <ResumeSessionDialog
                    resumeData={resumeData}
                    onContinue={handleContinueSession}
                    onStartNew={handleStartNewSession}
                    onClose={() => setShowResumeDialog(false)}
                />
            )}

            {/* History List */}
            {showHistory && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">Historial de Propuestas</h2>
                                <button
                                    onClick={handleHideHistory}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <HistoryList
                                userId={userId}
                                onSelectProposal={handleSelectProposal}
                                onContinueProposal={handleContinueProposal}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarketingChainWizard; 